import React, { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AiOutlineLaptop, AiOutlineConsoleSql } from "react-icons/ai";
import { TbSettingsSearch } from "react-icons/tb";
import { DiAppstore } from "react-icons/di";
import { useScrollBehavior } from './useScrollBehavior.js';
import './services_module.css';

const ServicesModule = ({ renderAnimatedLettersByWord }) => {
  const ref = useRef(null);
  const stickyRef2 = useRef(null);
  const overlayRef2 = useRef(null);

  const inView = useInView(ref, { once: true, amount: 0.2 });

  useScrollBehavior();

  useEffect(() => {
    const handleScroll4 = () => {
      if (!stickyRef2.current || !overlayRef2.current) return;

      const stickyRect = stickyRef2.current.getBoundingClientRect();
      const overlayRect = overlayRef2.current.getBoundingClientRect();

      if (stickyRect.top <= 0 && stickyRect.bottom > overlayRect.height) {
        overlayRef2.current.style.position = 'fixed';
        overlayRef2.current.style.top = '0';
      } else if (stickyRect.bottom <= overlayRect.height) {
        overlayRef2.current.style.position = 'absolute';
        overlayRef2.current.style.top = `${stickyRect.height - overlayRect.height}px`;
      } else {
        overlayRef2.current.style.position = 'absolute';
        overlayRef2.current.style.top = '0';
      }
    };

    window.addEventListener('scroll', handleScroll4, { passive: true });
    handleScroll4();

    return () => {
      window.removeEventListener('scroll', handleScroll4);
    };
  }, []);

  const services = [
    {
      to: '/services1',
      icon: <AiOutlineLaptop />,
      title: 'Web Design & Development',
      body: 'Crafting visually stunning, user friendly, and high performing websites tailored to your brand.'
    },
    {
      to: '/seo',
      icon: <TbSettingsSearch />,
      title: 'Search Engine Optimization (SEO)',
      body: 'Boosting your online visibility, driving organic traffic, and helping you dominate search rankings.'
    },
    {
      to: '/app',
      icon: <DiAppstore />,
      title: 'App Development (Mobile/Web Apps)',
      body: 'Building intuitive, robust, and scalable custom applications that streamline operations and engage your audience.'
    },
    {
      to: '/fullstack',
      icon: <AiOutlineConsoleSql />,
      title: 'Full-Stack Digital Solutions',
      body: 'From concept to launch and beyond, we handle every layer of your digital product. Our full-stack expertise ensures seamless integration and optimized performance.'
    }
  ];

  const container = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        staggerChildren: 0.12
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: 'easeOut' }
    }
  };

  return (
    <div id="servmod_wrap" ref={ref}>
      <div className="sticky-element-container2">
        <div ref={stickyRef2} className="background-div2">
          <div ref={overlayRef2} className="overlay-container2">
            <motion.div
              id="servmod_intro"
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              variants={container}
            >
              <motion.div id="servmod_kicker" variants={item}>
                Design • SEO • Apps • Full-Stack Strategy
              </motion.div>

              <motion.h1 id="servmod_h1" variants={item}>
                {renderAnimatedLettersByWord(
                  'How we empower your business online.',
                  inView
                )}
              </motion.h1>

              <motion.h3 id="servmod_h3" variants={item}>
                Tailoring solutions built for performance and growth.
              </motion.h3>
            </motion.div>
          </div>
          <div></div>
        </div>
      </div>

      <motion.div
        id="servmod_grid"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={container}
      >
        {services.map((service, index) => (
          <motion.div className="servmod_card" variants={item} key={index}>
            <Link to={service.to} className="servmod_card_link">
              <div className="servmod_icon">{service.icon}</div>
              <h2>{service.title}</h2>
              <p>{service.body}</p>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default ServicesModule;